import React, { useEffect, useMemo, useState } from 'react';
import { GitMerge, X } from 'lucide-react';
import { intakeAPI, patientAPI } from '../../services/api';
import { useConfirm, useToast } from '../UIProvider';
import useDialog from '../../hooks/useDialog';
import { formatPatientAge } from '../../utils/age';
import { formatClinicDate } from '../../utils/clinicTime';
import { describeGuardian } from '../../constants/guardians';

// The one place two files become one. Both are laid side by side, field by field, the person
// picks which file survives (the one with more history is offered first), and everything on the
// other file moves across: visits, prescriptions, bills, alerts, notes, credit, documents,
// guardians, tags, family, medical history. The retired MRN keeps resolving in search, so an old
// slip still finds the right file. Nothing is undone from the screen: the merge log at the bottom
// of the report says what moved, for someone to reverse by hand if it ever comes to that.
//
// The medical history never displaces the kept file's own. If the kept file has none, the other's
// becomes its history; if both have one, the other's is kept beside it as an earlier record.

const FIELDS = [
  { key: 'name', label: 'Name', value: (p) => `${p.firstName || ''} ${p.lastName || ''}`.trim() || '—' },
  { key: 'age', label: 'Age', value: (p) => formatPatientAge(p) || '—' },
  {
    key: 'dob', label: 'Date of birth',
    value: (p) => (p.dobEstimated ? 'estimated from an age' : formatClinicDate(p.dateOfBirth)),
  },
  { key: 'gender', label: 'Gender', value: (p) => (p.gender ? p.gender.charAt(0) + p.gender.slice(1).toLowerCase() : '—') },
  { key: 'father', label: 'Father', value: (p) => p.fatherName || '—' },
  { key: 'husband', label: 'Husband', value: (p) => p.husbandName || '—', onlyIf: (a, b) => a.husbandName || b.husbandName },
  { key: 'phone', label: 'Phone', value: (p) => p.phoneNumber || '—' },
  { key: 'cnic', label: 'CNIC', value: (p) => p.cnic || '—' },
  { key: 'bform', label: 'B-Form', value: (p) => p.bFormNumber || '—', onlyIf: (a, b) => a.bFormNumber || b.bFormNumber },
  {
    key: 'guardian', label: 'Guardian',
    value: (p) => (p.guardianName || p.guardianCnic
      ? [describeGuardian({ name: p.guardianName || 'Unnamed', relationship: p.guardianRelationship, relationshipOther: p.guardianRelationshipOther }), p.guardianCnic].filter(Boolean).join(' · ')
      : '—'),
    onlyIf: (a, b) => a.guardianName || b.guardianName || a.guardianCnic || b.guardianCnic,
  },
  { key: 'address', label: 'Address', value: (p) => [p.area, p.town, p.city].filter(Boolean).join(', ') || '—' },
  { key: 'registered', label: 'Registered', value: (p) => formatClinicDate(p.createdAt) },
  { key: 'status', label: 'Status', value: (p) => (p.status ? p.status.charAt(0) + p.status.slice(1).toLowerCase() : '—') },
  { key: 'history', label: 'Medical history', value: (p, h) => describeHistory(h) },
];

// A lookup that failed says nothing either way, so it shows as unknown rather than "Not taken".
function describeHistory(h) {
  if (!h) return '—';
  const earlier = Array.isArray(h.earlier) ? h.earlier.length : 0;
  const own = h.intake ? `Taken ${formatClinicDate(h.intake.completedAt)}` : 'Not taken';
  return earlier > 0 ? `${own}, plus ${earlier} earlier record${earlier === 1 ? '' : 's'}` : own;
}

const history = (visits, lastVisit) =>
  Number(visits || 0) === 0 ? 'No visits' : `${visits} visits, last ${lastVisit || 'unknown'}`;

const MergePatientsDialog = ({ pair, onClose, onMerged }) => {
  const dialogRef = useDialog(onClose, { confirmDiscard: false });
  const confirm = useConfirm();
  const notify = useToast();
  const [files, setFiles] = useState(null);
  const [keep, setKeep] = useState('a'); // 'a' is the report's Primary: more history, offered first
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    // The histories are read alongside, but a failed one does not stop the merge being offered.
    Promise.all([
      patientAPI.getById(pair.aId),
      patientAPI.getById(pair.bId),
      intakeAPI.forPatient(pair.aId).catch(() => null),
      intakeAPI.forPatient(pair.bId).catch(() => null),
    ])
      .then(([a, b, ha, hb]) => { if (alive) setFiles({ a, b, ha, hb }); })
      .catch(() => { if (alive) notify('error', 'Could not load the two files'); });
    return () => { alive = false; };
  }, [pair.aId, pair.bId, notify]);

  const sides = useMemo(() => ({
    a: { patient: files?.a, visits: pair.aVisits, lastVisit: pair.aLastVisit },
    b: { patient: files?.b, visits: pair.bVisits, lastVisit: pair.bLastVisit },
  }), [files, pair]);
  const kept = sides[keep];
  const gone = sides[keep === 'a' ? 'b' : 'a'];
  const nameOf = (side) => (side.patient ? `${side.patient.firstName || ''} ${side.patient.lastName || ''}`.trim() : '');
  const rows = files ? FIELDS.filter((f) => !f.onlyIf || f.onlyIf(files.a, files.b)) : [];

  const doMerge = async () => {
    if (!files) return;
    const ok = await confirm({
      title: `Merge into ${nameOf(kept)} (${kept.patient.mrn})?`,
      subtitle: `${gone.patient.mrn} will be retired`,
      message: `Everything on ${nameOf(gone)}, ${history(gone.visits, gone.lastVisit).toLowerCase()}, plus prescriptions, bills, alerts, notes, credit, documents, guardians and medical history, moves to ${nameOf(kept)}. ${gone.patient.mrn} stays searchable and points to ${kept.patient.mrn}. This cannot be undone from the screen.`,
      confirmText: 'Merge files',
      tone: 'danger',
    });
    if (!ok) return;
    setBusy(true);
    try {
      const result = await patientAPI.merge(kept.patient.id, gone.patient.id, note);
      notify('success', `${gone.patient.mrn} merged into ${kept.patient.mrn}`);
      onMerged(result);
    } catch (e) {
      notify('error', e?.message || 'Could not merge the files');
      setBusy(false);
    }
  };

  const SideHeader = ({ id, label }) => {
    const side = sides[id];
    const chosen = keep === id;
    return (
      <th className={`px-4 py-3 text-left align-top ${chosen ? 'bg-emerald-50' : ''}`}>
        <label className="flex items-start gap-2 cursor-pointer">
          <input type="radio" name="keep" checked={chosen} onChange={() => setKeep(id)} className="mt-1 accent-emerald-600" />
          <span>
            <span className="block text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</span>
            <span className="block text-base font-bold text-gray-900">{nameOf(side) || '…'}</span>
            <span className="block font-mono text-xs text-gray-500">{side.patient?.mrn || pair[`${id}Mrn`]}</span>
            <span className="block text-xs text-gray-500 mt-0.5">{history(side.visits, side.lastVisit)}</span>
            <span className={`block text-xs mt-1 font-medium ${chosen ? 'text-emerald-700' : 'text-gray-400'}`}>
              {chosen ? 'Keep this file' : 'Fold into the other'}
            </span>
          </span>
        </label>
      </th>
    );
  };

  return (
    <div ref={dialogRef} className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full mx-4 max-h-[95vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
              <GitMerge className="h-5 w-5 text-emerald-700" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Merge two files</h2>
              <p className="text-sm text-gray-600 mt-0.5">
                Pick the file to keep. Everything on the other moves across, and its MRN keeps pointing here.
              </p>
            </div>
          </div>
          <button type="button" aria-label="Close" onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {!files ? (
            <p className="text-sm text-gray-500">Loading both files…</p>
          ) : (
            <div className="overflow-x-auto border border-gray-200 rounded-xl">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 w-32">Field</th>
                    <SideHeader id="a" label="Primary" />
                    <SideHeader id="b" label="Candidate" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((f) => {
                    const va = f.value(files.a, files.ha);
                    const vb = f.value(files.b, files.hb);
                    const differs = va !== vb && va !== '—' && vb !== '—';
                    return (
                      <tr key={f.key} className="border-t border-gray-100">
                        <td className="px-4 py-2 text-gray-500">{f.label}</td>
                        <td className={`px-4 py-2 ${keep === 'a' ? 'bg-emerald-50/60' : ''} ${differs ? 'font-medium text-gray-900' : 'text-gray-700'}`}>{va}</td>
                        <td className={`px-4 py-2 ${keep === 'b' ? 'bg-emerald-50/60' : ''} ${differs ? 'font-medium text-gray-900' : 'text-gray-700'}`}>{vb}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <p className="text-sm text-gray-600">
            Identifiers and documents the kept file lacks are taken from the other; nothing on the
            kept file is overwritten. The merge is listed at the bottom of the report with what moved.
            {files?.ha?.intake && files?.hb?.intake && (
              <>
                {' '}Both files have a medical history: the kept file&apos;s stays its history, and the
                other&apos;s is kept beside it as an earlier record.
              </>
            )}
          </p>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Why these are one person</label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              maxLength={255}
              placeholder="e.g. same child, mother registered her again with a guessed birth date"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>
        </div>

        <div className="p-6 border-t border-gray-200 flex gap-3">
          <button type="button" onClick={onClose} className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium">
            Cancel
          </button>
          <button
            type="button"
            onClick={doMerge}
            disabled={!files || busy}
            className="flex-1 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            {busy ? 'Merging…' : `Merge into ${kept.patient ? kept.patient.mrn : 'the kept file'}`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MergePatientsDialog;
